import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tournament, User, fetchAllUsers, addManagerToTournament, removeManagerFromTournament } from '../../services/api';

interface ManageManagersDialogProps {
  open: boolean;
  onClose: () => void;
  tournament: Tournament;
}

const ManageManagersDialog = ({ open, onClose, tournament }: ManageManagersDialogProps) => {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Fetch all users (filter to tournament managers and admins)
  // Note: This requires admin access, but tournament managers who can manage this tournament
  // should be able to add other managers. If this fails, we'll show an error.
  const { data: allUsers, isLoading, error: fetchError } = useQuery({
    queryKey: ['all-users'],
    queryFn: fetchAllUsers,
    enabled: open,
    retry: false,
  });

  // Filter to only tournament managers and admins
  const eligibleManagers = (allUsers || []).filter(
    (user) => user.role === 'tournament_manager' || user.role === 'admin'
  );

  // Get current managers
  const currentManagerIds = tournament.manager_user_ids || [];
  const currentManagers = eligibleManagers.filter((user) => currentManagerIds.includes(user.id));

  // Filter out already-selected managers and creator
  const availableManagers = eligibleManagers.filter(
    (user) => !currentManagerIds.includes(user.id) && user.id !== tournament.created_by
  );

  const addManagerMutation = useMutation({
    mutationFn: (managerId: string) => addManagerToTournament(tournament.id, managerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', tournament.id] });
      setSelectedUser(null);
    },
  });

  const removeManagerMutation = useMutation({
    mutationFn: (managerId: string) => removeManagerFromTournament(tournament.id, managerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', tournament.id] });
    },
  });

  const handleAdd = () => {
    if (selectedUser) {
      addManagerMutation.mutate(selectedUser.id);
    }
  };

  const handleRemove = (managerId: string) => {
    removeManagerMutation.mutate(managerId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Tournament Managers</DialogTitle>
      <DialogContent>
        {!!fetchError && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2" color="error">
              Unable to load users. Only admins can manage tournament managers.
            </Typography>
          </Box>
        )}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Current Managers
          </Typography>
          {!fetchError && currentManagers.length > 0 ? (
            <List dense>
              {currentManagers.map((manager) => (
                <ListItem key={manager.id}>
                  <ListItemText
                    primary={manager.name}
                    secondary={manager.email}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="remove"
                      onClick={() => handleRemove(manager.id)}
                      size="small"
                      disabled={removeManagerMutation.isPending}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No additional managers assigned
            </Typography>
          )}
        </Box>

        {!fetchError && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Add Manager
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Autocomplete
                options={availableManagers}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                loading={isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Manager"
                    placeholder="Search for tournament manager or admin"
                    fullWidth
                  />
                )}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={!selectedUser || addManagerMutation.isPending || isLoading}
              >
                Add
              </Button>
            </Box>
            {availableManagers.length === 0 && !isLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No available managers to add
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageManagersDialog;

